import { useQuery } from '@tanstack/react-query'
import ms from 'ms'

import { useCurrentStore } from '@hooks/useCurrentStore'
import { useScan } from '@hooks/useScan'
import { useLayoutTransformed } from '@state/layout/queries'
import { toLayoutPose } from '@state/shelf/transformations/geometric'

import p10 from './assets/10_panorama_image.jpeg'
import p12 from './assets/12_panorama_image.jpeg'
import p14 from './assets/14_panorama_image.jpeg'
import p16 from './assets/16_panorama_image.jpeg'
import p1 from './assets/1_panorama_image.jpeg'
import p22 from './assets/22_panorama_image.jpeg'
import p23 from './assets/23_panorama_image.jpeg'
import p2 from './assets/2_panorama_image.jpeg'
import p5 from './assets/5_panorama_image.jpeg'
import p8 from './assets/8_panorama_image.jpeg'
import img000 from './assets/d4c9-2026-10-06/img000.jpeg'
import img002 from './assets/d4c9-2026-10-06/img002.jpeg'
import img006 from './assets/d4c9-2026-10-06/img006.jpeg'
import img013 from './assets/d4c9-2026-10-06/img013.jpeg'
import img014 from './assets/d4c9-2026-10-06/img014.jpeg'
import img021 from './assets/d4c9-2026-10-06/img021.jpeg'
import img022 from './assets/d4c9-2026-10-06/img022.jpeg'
import img026 from './assets/d4c9-2026-10-06/img026.jpeg'
import img030 from './assets/d4c9-2026-10-06/img030.jpeg'
import img032 from './assets/d4c9-2026-10-06/img032.jpeg'
import img033 from './assets/d4c9-2026-10-06/img033.jpeg'
import img037 from './assets/d4c9-2026-10-06/img037.jpeg'
import img038 from './assets/d4c9-2026-10-06/img038.jpeg'
import img040 from './assets/d4c9-2026-10-06/img040.jpeg'
import img043 from './assets/d4c9-2026-10-06/img043.jpeg'
import img044 from './assets/d4c9-2026-10-06/img044.jpeg'

import type { LayoutFrame } from '@state/layout/types'
import type { Pose2D } from '@utils/geometry'

export interface PanoramaImage {
  id: string
  x: number
  y: number
  url: string
  yaw?: number
}

/** A panorama as the robot records it: its pose in the robot's map frame, the scan's frame. */
interface RobotPanorama {
  id: string
  url: string
  pose: Pose2D
}

/** A scan uses the newest set whose `validFrom` is not after the scan date; list sets oldest first. */
type PanoramaSet = { validFrom: Date } & (
  | { frame: 'layout'; panoramas: PanoramaImage[] }
  | { frame: 'robot'; panoramas: RobotPanorama[] }
)

interface PanoramaStore {
  storeName: string
  sets: PanoramaSet[]
}

// Hand-placed in layout coordinates.
const panoramasD4C9 = [
  { id: '1', x: 27, y: 17, url: p1, yaw: 30 },
  { id: '2', x: 23, y: 14, url: p2, yaw: 290 },
  { id: '5', x: 27, y: 9.5, url: p5, yaw: 50 },
  { id: '8', x: 30.5, y: 7, url: p8, yaw: 290 },
  { id: '10', x: 29.5, y: 1.5, url: p10, yaw: 150 },
  { id: '12', x: 27, y: 6, url: p12, yaw: 230 },
  { id: '14', x: 23, y: 9, url: p14, yaw: 225 },
  { id: '16', x: 19.5, y: 11.5, url: p16, yaw: 300 },
  { id: '22', x: 9, y: 17, url: p22, yaw: 10 },
  { id: '23', x: 3, y: 16, url: p23, yaw: 350 },
]

// From dm_D4C9_2026-10-06_20-06-20_panoramas.xml (robot ub6-02-290) as re-aligned to the scan's frame
// (aligned.xml, 2026-10-07), a subset of the 46 images.
const panoramasD4C9Ub6 = [
  { id: 'img000', url: img000, pose: { x: -0.99034, y: -1.1221, rotationRad: -2.0793 } },
  { id: 'img002', url: img002, pose: { x: 6.1498, y: -5.8913, rotationRad: 0.7833 } },
  { id: 'img006', url: img006, pose: { x: 10.698, y: -5.8513, rotationRad: 0.3570 } },
  { id: 'img013', url: img013, pose: { x: 12.075, y: 13.296, rotationRad: 0.0141 } },
  { id: 'img014', url: img014, pose: { x: 13.823, y: 9.4784, rotationRad: -0.9909 } },
  { id: 'img021', url: img021, pose: { x: 12.958, y: 4.7792, rotationRad: 2.3253 } },
  { id: 'img022', url: img022, pose: { x: 7.2772, y: 11.629, rotationRad: 2.0902 } },
  { id: 'img026', url: img026, pose: { x: 14.528, y: -2.2756, rotationRad: -1.2268 } },
  { id: 'img030', url: img030, pose: { x: 2.5926, y: 8.9672, rotationRad: -2.0845 } },
  { id: 'img032', url: img032, pose: { x: 6.5002, y: 3.5064, rotationRad: -1.3685 } },
  { id: 'img033', url: img033, pose: { x: 9.7419, y: -1.0097, rotationRad: -0.9954 } },
  { id: 'img037', url: img037, pose: { x: 18.515, y: -8.1454, rotationRad: -1.5887 } },
  { id: 'img038', url: img038, pose: { x: 15.679, y: -15.867, rotationRad: -2.6479 } },
  { id: 'img040', url: img040, pose: { x: 6.6946, y: -15.859, rotationRad: -0.7853 } },
  { id: 'img043', url: img043, pose: { x: 2.6143, y: -16.548, rotationRad: -2.4240 } },
  { id: 'img044', url: img044, pose: { x: 2.06, y: -6.4864, rotationRad: 1.6802 } },
]

const setsD4C9: PanoramaSet[] = [
  { validFrom: new Date(0), frame: 'layout', panoramas: panoramasD4C9 },
  { validFrom: new Date('2026-10-06'), frame: 'robot', panoramas: panoramasD4C9Ub6 },
]

const stores: PanoramaStore[] = [
  { storeName: 'D4C9', sets: setsD4C9 }, // name of D4C9 on dev-ureka.ubica.ai and local
  { storeName: 'Drogeriemarkt', sets: setsD4C9 }, // name of D4C9 on dev-ureka.ubica.ai and local
]

// A relative angle of 0 renders at three quarters of the image width (see Dome), measured clockwise
// from layout +y (see calculateRelativeAngle). The robot's Theta X faces backwards, which puts the
// robot's heading on the image seam, a further quarter turn away.
const ROBOT_PANORAMA_YAW_OFFSET_DEG = 180

function toPanoramaImage({ id, url, pose }: RobotPanorama, frame: LayoutFrame): PanoramaImage {
  const { x, y, rotationRad } = toLayoutPose(pose, frame)
  return { id, url, x, y, yaw: ROBOT_PANORAMA_YAW_OFFSET_DEG - (rotationRad * 180) / Math.PI }
}

function usePanoramaSet() {
  const store = useCurrentStore()
  const { scanDate } = useScan()

  const sets = stores.find(panoramaStore => panoramaStore.storeName === store.name)?.sets ?? []
  return sets.filter(set => set.validFrom <= scanDate).at(-1)
}

export function usePanorama() {
  const set = usePanoramaSet()
  const { id: scanId } = useScan()
  const { data: layout } = useLayoutTransformed({ complete: false })

  return useQuery<PanoramaImage[]>({
    queryKey: ['panoramas', scanId, set?.validFrom],
    queryFn: async () => {
      if (!set) {
        return []
      }

      if (set.frame === 'layout') {
        return set.panoramas
      }

      return set.panoramas.map(panorama => toPanoramaImage(panorama, layout!.frame))
    },
    staleTime: ms('1d'),
    enabled: layout !== undefined,
  })
}

export function useArePanoramasAvailable() {
  const set = usePanoramaSet()
  return set !== undefined && set.panoramas.length > 0
}
